import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Space, message, Tabs } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  MailOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/services/api';
import type { LoginRequest } from '@/types/auth';
import './Login.css';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, loginWithEmail, loginWithPhone, isLoading, error, clearError, isAuthenticated } =
    useAuthStore();

  // Tab state
  const [activeTab, setActiveTab] = useState('username');

  // Verification code state
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  // Show error message
  useEffect(() => {
    if (error) {
      message.error(error);
      clearError();
    }
  }, [error, clearError]);

  // Countdown timer for verification code
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle username/password login
  const handleUsernameLogin = async (values: LoginRequest) => {
    try {
      await login(values);
      message.success('登录成功！');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  // Handle email/password login
  const handleEmailLogin = async (values: { email: string; password: string }) => {
    try {
      await loginWithEmail(values.email, values.password);
      message.success('登录成功！');
      navigate('/dashboard');
    } catch (error) {
      console.error('Email login failed:', error);
    }
  };

  // Handle phone/code login
  const handlePhoneLogin = async (values: { phone: string; code: string }) => {
    try {
      await loginWithPhone(values.phone, values.code);
      message.success('登录成功！');
      navigate('/dashboard');
    } catch (error) {
      console.error('Phone login failed:', error);
    }
  };

  // Send verification code
  const handleSendCode = async () => {
    try {
      const phone = form.getFieldValue('phone');
      if (!phone) {
        message.error('请先输入手机号');
        return;
      }

      // Validate phone format
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(phone)) {
        message.error('请输入有效的手机号');
        return;
      }

      setSendingCode(true);
      const response = await authApi.sendVerificationCode('phone', phone, 'login');

      // Development mode - show code in message
      if (import.meta.env.DEV && response.data.code) {
        message.success(`验证码已发送！开发模式：${response.data.code}`, 10);
      } else {
        message.success('验证码已发送，请查收短信');
      }

      setCountdown(60);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  // Form submission router
  const handleSubmit = (values: any) => {
    switch (activeTab) {
      case 'username':
        handleUsernameLogin(values);
        break;
      case 'email':
        handleEmailLogin(values);
        break;
      case 'phone':
        handlePhoneLogin(values);
        break;
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-gradient" />
      </div>

      <div className="login-content">
        <Card className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <SafetyOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </div>
            <Title level={2} className="login-title">
              O2World 管理后台
            </Title>
            <Text type="secondary" className="login-subtitle">
              欢迎来到 AI 世界管理平台
            </Text>
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            centered
            items={[
              {
                key: 'username',
                label: (
                  <span>
                    <UserOutlined />
                    用户名
                  </span>
                ),
              },
              {
                key: 'email',
                label: (
                  <span>
                    <MailOutlined />
                    邮箱
                  </span>
                ),
              },
              {
                key: 'phone',
                label: (
                  <span>
                    <MobileOutlined />
                    手机号
                  </span>
                ),
              },
            ]}
          />

          <Form
            form={form}
            name="login"
            onFinish={handleSubmit}
            autoComplete="off"
            layout="vertical"
            size="large"
            style={{ marginTop: '24px' }}
          >
            {/* Username login */}
            {activeTab === 'username' && (
              <>
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: '请输入用户名' },
                    { min: 3, message: '用户名至少3个字符' },
                    { max: 50, message: '用户名最多50个字符' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="用户名"
                    autoComplete="username"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少6个字符' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                    autoComplete="current-password"
                  />
                </Form.Item>
              </>
            )}

            {/* Email login */}
            {activeTab === 'email' && (
              <>
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' },
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="邮箱地址" autoComplete="email" />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: '请输入密码' },
                    { min: 6, message: '密码至少6个字符' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="密码"
                    autoComplete="current-password"
                  />
                </Form.Item>
              </>
            )}

            {/* Phone login */}
            {activeTab === 'phone' && (
              <>
                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: '请输入手机号' },
                    { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
                  ]}
                >
                  <Input prefix={<MobileOutlined />} placeholder="手机号" autoComplete="tel" />
                </Form.Item>

                <Form.Item
                  name="code"
                  rules={[
                    { required: true, message: '请输入验证码' },
                    { len: 6, message: '验证码为6位数字' },
                    { pattern: /^\d{6}$/, message: '验证码必须是6位数字' },
                  ]}
                >
                  <Input
                    placeholder="验证码"
                    suffix={
                      <Button
                        type="link"
                        disabled={countdown > 0}
                        loading={sendingCode}
                        onClick={handleSendCode}
                        style={{ padding: 0 }}
                      >
                        {countdown > 0 ? `${countdown}秒后重发` : '发送验证码'}
                      </Button>
                    }
                  />
                </Form.Item>
              </>
            )}

            <Form.Item style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Link to="/forgot-password" style={{ fontSize: '14px' }}>
                  忘记密码?
                </Link>
              </div>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block size="large">
                登录
              </Button>
            </Form.Item>
          </Form>

          <div className="login-footer">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
            </Space>
          </div>
        </Card>
      </div>

      <div className="login-footer-links">
        <Text type="secondary" style={{ fontSize: '12px' }}>
          © 2025 O2World AI World Platform. All rights reserved.
        </Text>
      </div>
    </div>
  );
};

export default Login;
